import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { useScreenSubject } from '@/contexts/ScreenSubjectContext';
import { WARNING_BANNER_ACTION_CLASS, WarningBanner } from './WarningBanner';

/**
 * «Your access could not be read» (#1677) — for both shells (the portal's
 * AppShell, the Bitrix24 layout), from the subject either one provides.
 *
 * Shown only while the decision is UNKNOWN: `/access/me` was asked, failed
 * after its retries, and no earlier answer is in hand (`AccessState` failed
 * with `retry`). Then the role decides meanwhile — for a grant holder that
 * means screens its grant does not cover, each answered by the API with a
 * refusal — and the banner is the only explanation it gets, with a way to ask
 * again.
 *
 * Not shown to a subject whose access is known: an answer in hand (a later
 * background failure keeps it), a platform owner (always without a grant),
 * nothing asked (no organization or session). A subject without a grant whose
 * read fails is indistinguishable from one with a grant until the read
 * succeeds — the banner says «could not check», never «you have a grant»
 * nor «your access settings», which only a grant holder has.
 *
 * «Try again» is disabled while the read it started is on its way: a second
 * click would only join it.
 */
export function AccessReadFailedBanner() {
  const { t } = useTranslation('common');
  const subject = useScreenSubject();
  const [retrying, setRetrying] = useState(false);
  if (!subject || subject.isOwner) return null;
  const { access } = subject;
  if (access.status !== 'failed' || !access.retry) return null;
  const retry = access.retry;
  const onRetry = () => {
    setRetrying(true);
    void retry().finally(() => setRetrying(false));
  };

  return (
    <WarningBanner
      icon={ShieldAlert}
      data-testid="access-read-failed-banner"
      message={t(
        'accessRead.failed',
        'Could not check your access — some sections may be unavailable.'
      )}
      action={
        <button
          type="button"
          data-testid="access-read-failed-retry"
          onClick={onRetry}
          disabled={retrying}
          aria-busy={retrying}
          className={WARNING_BANNER_ACTION_CLASS}
        >
          {retrying
            ? t('accessRead.retrying', 'Checking…')
            : t('accessRead.retry', 'Try again')}
        </button>
      }
    />
  );
}
